import { useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import ScrollToTop from "./common/ScrollToTop";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AppSidebar from "./app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "./ui/sidebar";
import { TooltipProvider } from "./ui/tooltip";
import { useLanguage } from "context/languageContext";
import { translations, isRtlLanguage } from "utils/i18n";

const AUTH_ROUTE_PREFIXES = [
  "/login",
  "/signup",
  "/verify-email",
  "/onboarding",
];

// react-toastify's built-in close icon is a squat, oddly-proportioned glyph
// baked into an SVG path — swapped for the same lucide X used everywhere
// else in the app so it actually matches.
function ToastCloseButton({ closeToast }) {
  const { language } = useLanguage();
  return (
    <button
      type="button"
      className="Toastify__close-button"
      onClick={closeToast}
      aria-label={translations[language].ui.close}
    >
      <X size={12} strokeWidth={2} />
    </button>
  );
}

// Always rendered — the toggle lives here permanently (not inside the
// sidebar; see app-sidebar.js), so it's one button that tracks the
// sidebar's edge as it resizes rather than jumping between two spots.
function ContentHeader({ title }) {
  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-3">
      <SidebarTrigger />
      <span className="text-sm font-semibold text-foreground">{title}</span>
    </header>
  );
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const { language } = useLanguage();
  const tr = translations[language].sidebarChrome;
  const isAuthPage = AUTH_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  const inProjects = pathname.startsWith("/projects") || pathname.startsWith("/__preview/projects");
  // Same reasoning as inProjects: the transcript editor carries its own
  // SidebarTrigger in its (thicker) own header, so a second "Home" bar above
  // it would just be redundant chrome. Matches both the plain editor and its
  // caption (video preview) counterpart.
  const inTranscriptEditor = /^\/(__preview\/)?(transcribe|caption)\/[^/]+\/?$/.test(pathname);
  const contentTitle =
    pathname.startsWith("/settings") || pathname.startsWith("/__preview/settings")
      ? tr.contentTitleSettings
      : tr.contentTitleHome;

  // Projects brings its own secondary panel (see ProjectsPanel), so it gets
  // the icon rail instead of the labelled one — leaving Projects returns the
  // sidebar to its expanded state. Only the route *boundary* sets this, so
  // a manual toggle in between isn't fought on the next render. Ported from
  // arkii-product's AppLayout, which does the same for its Library section.
  const [sidebarOpen, setSidebarOpen] = useState(!inProjects);
  const [syncedWithProjects, setSyncedWithProjects] = useState(inProjects);
  if (syncedWithProjects !== inProjects) {
    setSyncedWithProjects(inProjects);
    setSidebarOpen(!inProjects);
  }

  return (
    <div className="app-shell">
      <ScrollToTop />
      {isAuthPage ? (
        children
      ) : (
        <TooltipProvider>
          {/* h-svh, not flex-1/min-h-0: .app-shell below is only min-height:100vh
              (auth pages need to grow taller and scroll normally), so nothing
              in that chain ever hard-caps height to the viewport on its own.
              Every "scrollable" region inside (this page's own overflow-y-auto
              panes) needs one real anchor to bound against, or long content
              just grows the whole html/body instead of scrolling internally —
              which also drags anything meant to stay pinned (a bottom player
              bar, a sticky header) along with it. */}
          <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen} className="h-svh">
            <AppSidebar />
            <SidebarInset className="min-h-0 min-w-0 overflow-hidden">
              {/* Projects' own panel (ProjectsPanel) carries the trigger
                  itself — a second header row here would just cut the panel
                  short at its top edge instead of letting it run full height. */}
              {!inProjects && !inTranscriptEditor && <ContentHeader title={contentTitle} />}
              <div className="min-h-0 flex-1 overflow-auto">{children}</div>
            </SidebarInset>
          </SidebarProvider>
        </TooltipProvider>
      )}
      <ToastContainer
        position={isRtlLanguage(language) ? "top-left" : "top-right"}
        autoClose={4000}
        theme="dark"
        newestOnTop
        closeOnClick
        hideProgressBar
        closeButton={ToastCloseButton}
      />
    </div>
  );
}
